import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  ExtapiInvoiceRow,
  RmaInvoice,
  RmaInvoiceRow,
} from '../../../Types/MyPages/InvoicesModel';
import { Dialog, Transition } from '@headlessui/react';
import { translate } from '../../../Services/translation';
import { getInvoiceRows } from '../../../Actions/Invoices.action';
import Checkbox from '../../Inputs/Checkbox';
import MyPagesRmaRowQuantityInput from './MyPagesRmaRowQuantityInput';
import MyPagesRmaRowCommentBox from './MyPagesRmaRowCommentBox';
import LoadingSpinner from '../../LoadingSpinner';
import clsx from 'clsx';

interface MyPagesRmaRowsModalProps {
  invoice: RmaInvoice;
  onComplete: (invoice: RmaInvoice) => void;
  onClose: () => void;
}

const MyPagesRmaRowsModal = ({
  invoice,
  onComplete,
  onClose,
}: MyPagesRmaRowsModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>(
    invoice.rows?.flatMap((x) => x.id) ?? []
  );
  const [invoiceRows, setInvoiceRows] = useState<
    RmaInvoiceRow[] | undefined
  >(invoice.rows);
  const headerHeight = useMemo(
    () => document.querySelector('#header')?.clientHeight,
    []
  );
  const fetchRows = async () => {
    setIsLoading(true);
    try {
      const data = await getInvoiceRows(invoice.documentNo);
      const rows = Object.values(data.invoiceRows).reduce(
        (prev: RmaInvoiceRow[], curr: RmaInvoiceRow[]) => [
          ...prev,
          ...curr,
        ],
        []
      ) as RmaInvoiceRow[];

      for (let row of rows) {
        row.returnQuantity = row.pickedQuantity;
      }

      setInvoiceRows([
        ...rows.filter((x) => !invoiceRows?.some((y) => y.id == x.id)),
        ...(invoiceRows ?? []),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const addRow = (row: RmaInvoiceRow) => {
    setSelectedRowIds((selectedRowIds) => {
      return [...selectedRowIds, row.id];
    });
  };

  const removeRow = (row: RmaInvoiceRow) => {
    const rowIndex = selectedRowIds.indexOf(row.id);
    if (rowIndex === -1) return;

    setSelectedRowIds((selectedRowIds) => {
      const rowIdsCopy = [...selectedRowIds];
      rowIdsCopy.splice(rowIndex, 1);
      return rowIdsCopy;
    });
  };

  const toggleAll = () => {
    if (!invoiceRows) return;

    if (invoiceRows.length == selectedRowIds.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds((selectedRowIds) => {
        return [
          ...selectedRowIds,
          ...invoiceRows
            .filter((x) => selectedRowIds.indexOf(x.id) === -1)
            .flatMap((x) => x.id),
        ];
      });
    }
  };

  const updateRow = (row: RmaInvoiceRow) => {
    if (!invoiceRows) return;

    const rowIndex = invoiceRows.findIndex((x) => x.id == row.id);
    if (rowIndex === -1) return;
    const invoiceRowsCopy = [...invoiceRows];
    invoiceRowsCopy.splice(rowIndex, 1, row);
    setInvoiceRows(invoiceRowsCopy);
  };

  const onRowQuantityChanged = (row: RmaInvoiceRow, quantity: number) => {
    updateRow({ ...row, returnQuantity: quantity });
  };

  const onRowCommentChanged = (row: RmaInvoiceRow, comment: string) => {
    updateRow({ ...row, reason: comment });
  };

  const closeDialog = () => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  const completeInvoice = () => {
    if (!invoiceRows) return;

    const invoiceCopy = { ...invoice };
    invoiceCopy.rows = invoiceRows.filter(
      (x) => selectedRowIds.includes(x.id) && x.returnQuantity > 0
    ) as ExtapiInvoiceRow[] & RmaInvoiceRow[];
    onComplete(invoiceCopy);
    closeDialog();
  };

  return (
    <Transition appear show={isVisible} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeDialog}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div
          className="fixed inset-0 overflow-y-auto"
          style={{ top: headerHeight }}
        >
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-[1024px] transform overflow-hidden bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold leading-6 text-gray-900"
                  >
                    {invoice.documentNo}
                  </Dialog.Title>
                  {invoiceRows && invoiceRows.length > 0 && (
                    <button
                      className="btn btn--outline btn--xsmall"
                      onClick={toggleAll}
                    >
                      {invoiceRows.length != selectedRowIds.length
                        ? translate('general.select-all')
                        : translate('general.deselect-all')}
                    </button>
                  )}
                </div>
                <div
                  className="mt-2 h-screen overflow-auto"
                  style={{
                    maxHeight: `calc((100vh - ${headerHeight}px) / 2)`,
                  }}
                >
                  {isLoading && (
                    <div className="flex h-full items-center justify-center">
                      <LoadingSpinner className="h-10 w-10" />
                    </div>
                  )}
                  {!isLoading &&
                    (!invoiceRows || invoiceRows.length == 0) && (
                      <div className="flex h-full items-center justify-center">
                        <small className="text-error-500">
                          {translate('general.error')}
                        </small>
                      </div>
                    )}
                  {!isLoading && invoiceRows && invoiceRows.length > 0 && (
                    <>
                      {/* Header */}
                      <div className="hidden grid-cols-8 gap-2 text-sm font-bold lg:grid">
                        <span className="ml-1.5 overflow-hidden text-ellipsis whitespace-nowrap">
                          {translate('mypage.rma.itemnumber')}
                        </span>
                        <span className="col-span-3 overflow-hidden text-ellipsis whitespace-nowrap">
                          {translate('mypage.rma.productname')}
                        </span>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-right">
                          {translate('mypage.invoices.unitprice')}
                        </span>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-right">
                          {translate('mypage.invoices.lineprice')}
                        </span>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-right">
                          {translate('mypage.rma.quantity')}
                        </span>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-right">
                          {translate('mypage.rma.return')}
                        </span>
                      </div>
                      {/* Rows */}
                      {invoiceRows?.map((row, idx) => (
                        <MyPagesRmaRowDetail
                          key={row.id + '-rma-' + idx}
                          row={row}
                          isSelected={selectedRowIds.includes(row.id)}
                          onToggle={(row, checked) =>
                            checked ? addRow(row) : removeRow(row)
                          }
                          onCommentChanged={onRowCommentChanged}
                          onQuantityChanged={onRowQuantityChanged}
                        />
                      ))}
                    </>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-end gap-4">
                  <button
                    type="button"
                    className="btn btn--outline btn--small"
                    onClick={closeDialog}
                  >
                    {translate('general.cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--small"
                    onClick={completeInvoice}
                  >
                    {translate('general.save')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

interface RmaInvoiceRowDetailProps {
  row: RmaInvoiceRow;
  isSelected: boolean;
  onToggle: (row: RmaInvoiceRow, selected: boolean) => void;
  onQuantityChanged: (row: RmaInvoiceRow, quantity: number) => void;
  onCommentChanged: (row: RmaInvoiceRow, comment: string) => void;
}
const MyPagesRmaRowDetail = ({
  row,
  isSelected,
  onToggle,
  onQuantityChanged,
  onCommentChanged,
}: RmaInvoiceRowDetailProps) => {
  return (
    <>
      {/* Mobile */}
      <div
        key={`invoice-mobile-${row.id}`}
        className={clsx(
          'mb-4 block rounded border border-solid border-gray-300 p-4 lg:hidden',
          isSelected ? 'bg-warning-50' : 'bg-white'
        )}
      >
        <label
          htmlFor={row.id + '-check-mobile'}
          className="cursor-pointer"
        >
          <div className="mb-3 flex justify-between">
            <p className="text-md font-bold">
              {translate('mypage.invoices.articlenumber')}:{' '}
              {row.articleNumber}
            </p>
            <div className="text-right">
              <Checkbox
                inputSize="small"
                type="checkbox"
                id={row.id + '-check-mobile'}
                onChange={(e) => onToggle(row, e.target.checked)}
                checked={isSelected}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
            <div className="flex flex-col">
              <p className="text-sm uppercase text-gray-600">
                {translate('mypage.invoices.productname')}
              </p>
              <p className="text-md">{row.description}</p>
            </div>
            <div className="flex flex-col">
              <p className="text-sm uppercase text-gray-600">
                {translate('mypage.invoices.invoicequantity')}
              </p>
              <p className="text-md">{row.pickedQuantity}</p>
            </div>
            <div className="flex flex-col">
              <p className="text-sm uppercase text-gray-600">
                {translate('mypage.invoices.unitprice')}
              </p>
              <p className="text-md">{row.unitPrice}</p>
            </div>
            <div className="flex flex-col">
              <p className="text-sm uppercase text-gray-600">
                {translate('mypage.invoices.lineprice')}
              </p>
              <p className="text-md">{row.lineAmount}</p>
            </div>
          </div>
        </label>
        {isSelected && (
          <div className="mt-2 grid grid-cols-4">
            <div className="col-span-3">
              <MyPagesRmaRowCommentBox
                value={row.reason}
                onChange={(value) => onCommentChanged(row, value)}
              />
            </div>
            <MyPagesRmaRowQuantityInput
              value={row.returnQuantity}
              maxValue={row.pickedQuantity}
              onChange={(value) => onQuantityChanged(row, value)}
            />
          </div>
        )}
      </div>

      {/* Desktop */}
      <div
        className={clsx(
          'mb-2 hidden border-b border-solid border-b-gray-100 lg:block',
          isSelected && 'bg-warning-50'
        )}
      >
        <label
          htmlFor={row.id + '-check'}
          className="grid cursor-pointer grid-cols-8 gap-2 p-2 text-sm last:mb-0"
        >
          <div>{row.articleNumber}</div>
          <div className="col-span-3">{row.description}</div>
          <div className="text-right">{row.unitPrice}</div>
          <div className="text-right">{row.lineAmount}</div>
          <div className="text-right">{row.pickedQuantity}</div>
          <span className="text-right">
            <Checkbox
              inputSize="small"
              type="checkbox"
              id={row.id + '-check'}
              onChange={(e) => onToggle(row, e.target.checked)}
              checked={isSelected}
            />
          </span>
        </label>

        {isSelected && (
          <div className="mt-2 grid grid-cols-4">
            <div className="col-span-3">
              <MyPagesRmaRowCommentBox
                value={row.reason}
                onChange={(value) => onCommentChanged(row, value)}
              />
            </div>
            <MyPagesRmaRowQuantityInput
              value={row.returnQuantity}
              maxValue={row.pickedQuantity}
              onChange={(value) => onQuantityChanged(row, value)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default MyPagesRmaRowsModal;
